import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Grid, Snackbar, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { boxApi } from '@/services/box.api';
import { LoadingState } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { BoxChoiceCard } from '@/components/box/BoxChoiceCard';
import { BoxWizardMenu } from '@/components/box/BoxWizardMenu';
import type { BoxMenu, BoxMenuItem, BoxPackage, UnionOption } from '@/types';

/** Scelta normalizzata (pacchetto o opzione unione) per il rendering della griglia. */
interface Choice {
  id: string;
  title: string;
  subtitle: string | null;
  priceDiff: number;
  isDefault: boolean;
  selected: boolean;
  onSelect: () => void;
}

export default function BoxConfiguratorPage() {
  const { id } = useParams<{ id: string }>();
  const configId = Number(id);
  const navigate = useNavigate();

  const [menu, setMenu] = useState<BoxMenu | null>(null);
  const [currentKey, setCurrentKey] = useState<string | null>(null);
  const [choices, setChoices] = useState<Choice[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [choicesLoading, setChoicesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const current: BoxMenuItem | undefined = menu?.items.find((i) => i.key === currentKey);

  const loadMenu = (): void => {
    setIsLoading(true);
    setError(null);
    boxApi
      .getMenu(configId)
      .then((m) => {
        setMenu(m);
        setCurrentKey((prev) => prev ?? m.items.find((i) => !i.answered)?.key ?? m.items[0]?.key ?? null);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Impossibile caricare il configuratore box. Riprova.');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (Number.isNaN(configId)) {
      setError('Configurazione non valida.');
      setIsLoading(false);
      return;
    }
    loadMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]);

  /** Applica il menu aggiornato dopo un salvataggio e avanza alla prossima voce non risposta. */
  const applyUpdatedMenu = (updated: BoxMenu, answeredKey: string): void => {
    setMenu(updated);
    const idx = updated.items.findIndex((i) => i.key === answeredKey);
    const next = updated.items.slice(idx + 1).find((i) => !i.answered) ?? updated.items.find((i) => !i.answered);
    if (next) setCurrentKey(next.key);
  };

  // Carica le scelte (pacchetti o opzioni unione) per la voce corrente.
  useEffect(() => {
    if (!current) {
      setChoices(null);
      return;
    }
    let alive = true;
    setChoicesLoading(true);
    const item = current;

    const buildFromPackages = (packages: BoxPackage[]): Choice[] =>
      packages.map((p) => ({
        id: `pkg-${p.packageId}`,
        title: p.name,
        subtitle: p.title,
        priceDiff: p.priceDiff,
        isDefault: p.isDefault,
        selected: item.selection != null ? p.name === item.selection : p.isDefault,
        onSelect: () => savePackage(item, p.packageId),
      }));

    const buildFromUnions = (options: UnionOption[]): Choice[] =>
      options.map((o) => ({
        id: `union-${o.key}`,
        title: o.name,
        subtitle: null,
        priceDiff: o.priceDiff,
        isDefault: o.isDefault,
        selected: item.selection != null ? o.name === item.selection : o.isDefault,
        onSelect: () => saveUnion(item, o.key),
      }));

    const request =
      item.type === 'UNION'
        ? boxApi.getUnionOptions(configId).then((r) => buildFromUnions(r.options))
        : boxApi.getPackages(item.substepId, item.boxId as number).then((r) => buildFromPackages(r.packages));

    request
      .then((c) => {
        if (alive) {
          setChoices(c);
          setChoicesLoading(false);
        }
      })
      .catch(() => {
        if (alive) {
          setChoices([]);
          setChoicesLoading(false);
          setToast('Impossibile caricare le opzioni. Riprova.');
        }
      });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, menu]);

  const savePackage = (item: BoxMenuItem, boxPackageId: number): void => {
    if (item.boxId == null) return;
    boxApi
      .saveAnswer(configId, { substepId: item.substepId, boxId: item.boxId, boxPackageId })
      .then((updated) => applyUpdatedMenu(updated, item.key))
      .catch(() => setToast('Impossibile salvare la scelta. Riprova.'));
  };

  const saveUnion = (item: BoxMenuItem, unionKey: string): void => {
    boxApi
      .saveAnswer(configId, { substepId: item.substepId, unionKey })
      .then((updated) => applyUpdatedMenu(updated, item.key))
      .catch(() => setToast('Impossibile salvare l’unione. Riprova.'));
  };

  return (
    <Box sx={{ flex: 1, px: { xs: 2, md: 6 }, py: 4 }}>
      <Button
        variant="text"
        color="inherit"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/box')}
        sx={{ mb: 1, pl: 0 }}
      >
        Le tue configurazioni box
      </Button>

      {isLoading && <LoadingState message="Caricamento configuratore box…" />}
      {error && <ErrorState message={error} onRetry={loadMenu} />}

      {!isLoading && !error && menu && (
        <>
          <Typography variant="h2" gutterBottom>
            {menu.name}
          </Typography>

          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={8}>
              {current ? (
                <>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
                    {current.groupLabel}
                  </Typography>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    {current.type === 'UNION' ? 'Unione dei box' : current.label}
                  </Typography>

                  {choicesLoading && <LoadingState message="Caricamento opzioni…" />}

                  {!choicesLoading && choices && (
                    <Grid container spacing={2}>
                      {choices.map((c) => (
                        <Grid item xs={12} sm={6} md={4} key={c.id}>
                          <BoxChoiceCard
                            title={c.title}
                            subtitle={c.subtitle}
                            priceDiff={c.priceDiff}
                            isDefault={c.isDefault}
                            selected={c.selected}
                            onSelect={c.onSelect}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {!choicesLoading && choices && choices.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Nessuna opzione disponibile per questo passo.
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Nessun passo da configurare per questa configurazione.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <BoxWizardMenu
                menu={menu}
                currentKey={currentKey ?? ''}
                onSelect={(item) => setCurrentKey(item.key)}
                onGoToResume={() => navigate(`/box/${configId}/resume`)}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Snackbar
        open={toast != null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
